import React, { useState, useContext } from 'react';
import {
  View, ScrollView, Dimensions, TouchableOpacity,
} from 'react-native';
import {
  Dialog,
  Portal,
  Button,
  Text,
  Searchbar,
  List,
  Avatar,
  Chip,
  IconButton,
  Divider,
  Menu,
} from 'react-native-paper';
import { useQuery, useMutation } from '@apollo/client';
import { Box, VStack, HStack } from 'native-base';
import { useToast } from 'react-native-toast-notifications';
import { ModalContext } from '../../context/ModalContext';
import LoadingIndicator from '../../components/commons/LoadingIndicator';
import TransportationVisualizerWithAssignments from '../../components/transportation/TransportationVisualizerWithAssignments';
import { SEAT_ASSIGNMENTS_FOR_TRIP, AVAILABLE_USERS_FOR_TRIP, TRIP_CAUTION_SUMMARY } from '../../gql/seat-assignment/query';
import {
  ASSIGN_SEAT, UNASSIGN_SEAT, MARK_CAUTION_PAID, MARK_CAUTION_RETURNED, RESET_CAUTION,
} from '../../gql/seat-assignment/mutation';
import { TripsForEvent } from '../../gql/trip/query';

function CautionIcon({
  assignment, onMarkPaid, onMarkReturned, onReset,
}) {
  const [menuVisible, setMenuVisible] = useState(false);

  const { cautionPaid, cautionReturned } = assignment;

  let iconName;
  let iconColor;
  if (cautionReturned) {
    iconName = 'cash-check';
    iconColor = '#4CAF50';
  } else if (cautionPaid) {
    iconName = 'cash-lock';
    iconColor = '#FF9800';
  } else {
    iconName = 'cash-remove';
    iconColor = '#9E9E9E';
  }

  return (
    <Menu
      visible={menuVisible}
      onDismiss={() => setMenuVisible(false)}
      anchor={(
        <IconButton
          icon={iconName}
          iconColor={iconColor}
          size={22}
          onPress={() => setMenuVisible(true)}
        />
      )}
    >
      {!cautionPaid && (
        <Menu.Item
          title={`Marquer comme payée (${assignment.cautionAmount}€)`}
          onPress={() => { setMenuVisible(false); onMarkPaid(); }}
        />
      )}
      {cautionPaid && !cautionReturned && (
        <>
          <Menu.Item
            title="Marquer comme restituée"
            onPress={() => { setMenuVisible(false); onMarkReturned(); }}
          />
          <Menu.Item
            title="Annuler le paiement"
            onPress={() => { setMenuVisible(false); onReset(); }}
          />
        </>
      )}
      {cautionReturned && (
        <Menu.Item
          title="Annuler la restitution"
          onPress={() => { setMenuVisible(false); onReset(); }}
        />
      )}
    </Menu>
  );
}

function ManageSeatAssignmentModal() {
  const toast = useToast();
  const { displayManageSeatAssignmentModal, toggleManageSeatAssignmentModal } = useContext(ModalContext);
  const { trip, eventId, onSave } = displayManageSeatAssignmentModal;

  const [selectedSeat, setSelectedSeat] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAssignedSeatsExpanded, setIsAssignedSeatsExpanded] = useState(false);

  const cautionRefetchQueries = [
    { query: SEAT_ASSIGNMENTS_FOR_TRIP, variables: { tripId: trip?.id } },
    { query: TRIP_CAUTION_SUMMARY, variables: { tripId: trip?.id } },
  ];

  // Queries
  const { data: seatData, loading: seatLoading } = useQuery(SEAT_ASSIGNMENTS_FOR_TRIP, {
    variables: { tripId: trip?.id },
    skip: !trip?.id,
  });

  const { data: usersData, loading: usersLoading } = useQuery(AVAILABLE_USERS_FOR_TRIP, {
    variables: { tripId: trip?.id },
    skip: !trip?.id,
  });

  const { data: cautionData } = useQuery(TRIP_CAUTION_SUMMARY, {
    variables: { tripId: trip?.id },
    skip: !trip?.id,
  });

  // Mutations
  const [assignSeat, { loading: assignLoading }] = useMutation(ASSIGN_SEAT, {
    refetchQueries: [
      { query: SEAT_ASSIGNMENTS_FOR_TRIP, variables: { tripId: trip?.id } },
      { query: AVAILABLE_USERS_FOR_TRIP, variables: { tripId: trip?.id } },
      { query: TRIP_CAUTION_SUMMARY, variables: { tripId: trip?.id } },
      { query: TripsForEvent, variables: { eventId } },
    ],
    onCompleted: () => {
      toast.show('Siège assigné avec succès', { type: 'success' });
      setSelectedSeat(null);
      setSelectedParticipant(null);
      onSave?.();
    },
    onError: (error) => {
      toast.show(error.message, { type: 'danger' });
    },
  });

  const [unassignSeat, { loading: unassignLoading }] = useMutation(UNASSIGN_SEAT, {
    refetchQueries: [
      { query: SEAT_ASSIGNMENTS_FOR_TRIP, variables: { tripId: trip?.id } },
      { query: AVAILABLE_USERS_FOR_TRIP, variables: { tripId: trip?.id } },
      { query: TRIP_CAUTION_SUMMARY, variables: { tripId: trip?.id } },
      { query: TripsForEvent, variables: { eventId } },
    ],
    onCompleted: () => {
      toast.show('Siège libéré avec succès', { type: 'success' });
      setSelectedSeat(null);
      onSave?.();
    },
    onError: (error) => {
      toast.show(error.message, { type: 'danger' });
    },
  });

  const [markCautionPaid] = useMutation(MARK_CAUTION_PAID, {
    refetchQueries: cautionRefetchQueries,
    onCompleted: () => toast.show('Caution encaissée', { type: 'success' }),
    onError: (e) => toast.show(e.message, { type: 'danger' }),
  });

  const [markCautionReturned] = useMutation(MARK_CAUTION_RETURNED, {
    refetchQueries: cautionRefetchQueries,
    onCompleted: () => toast.show('Caution restituée', { type: 'success' }),
    onError: (e) => toast.show(e.message, { type: 'danger' }),
  });

  const [resetCaution] = useMutation(RESET_CAUTION, {
    refetchQueries: cautionRefetchQueries,
    onCompleted: () => toast.show('Caution réinitialisée', { type: 'success' }),
    onError: (e) => toast.show(e.message, { type: 'danger' }),
  });

  const handleSeatPress = (seatNumber) => {
    setSelectedSeat(seatNumber);
    setSelectedParticipant(null);
  };

  const handleAssignSeat = (user) => {
    if (!selectedSeat) {
      toast.show('Veuillez sélectionner un siège', { type: 'warning' });
      return;
    }

    if (!user) {
      toast.show('Veuillez sélectionner un participant', { type: 'warning' });
      return;
    }

    assignSeat({
      variables: {
        input: {
          tripId: trip.id,
          playerId: user.id,
          seatNumber: selectedSeat,
        },
      },
    });
  };

  const handleUnassignSeat = (assignmentId) => {
    unassignSeat({
      variables: { id: assignmentId },
    });
  };

  const handleClose = () => {
    setSelectedSeat(null);
    setSelectedParticipant(null);
    setSearchQuery('');
    toggleManageSeatAssignmentModal();
  };

  if (!displayManageSeatAssignmentModal.display) return null;

  if (seatLoading || usersLoading) {
    return <LoadingIndicator text="Chargement des places..." />;
  }

  const seatAssignments = seatData?.seatAssignmentsForTrip || [];
  const availableUsers = usersData?.availableUsersForTrip || [];
  const cautionSummary = cautionData?.tripCautionSummary;

  // The server already excludes assigned users, but filter locally too for safety
  const assignedPlayerIds = new Set(seatAssignments.map((a) => a.playerId));
  if (trip?.driver?.id) assignedPlayerIds.add(trip.driver.id);
  const unassignedUsers = availableUsers.filter((u) => !assignedPlayerIds.has(u.id));

  // Apply search
  const matchesSearch = (u) => {
    const q = searchQuery.toLowerCase();
    return (
      u.firstName.toLowerCase().includes(q) || u.lastName.toLowerCase().includes(q)
    );
  };

  const filteredParticipants = unassignedUsers.filter(
    (u) => u.isParticipant && matchesSearch(u),
  );
  const filteredOthers = unassignedUsers.filter(
    (u) => !u.isParticipant && matchesSearch(u),
  );

  const currentSeatAssignment = seatAssignments.find((a) => a.seatNumber === selectedSeat);
  const isDriverSeat = selectedSeat === 1;

  // Caution banner colour
  let cautionBannerBg = 'gray.100';
  if (cautionSummary) {
    if (cautionSummary.cautionReturnedCount === cautionSummary.totalSeats && cautionSummary.totalSeats > 0) {
      cautionBannerBg = 'green.50';
    } else if (cautionSummary.cautionPaidCount === cautionSummary.totalSeats && cautionSummary.totalSeats > 0) {
      cautionBannerBg = 'green.50';
    } else if (cautionSummary.cautionPaidCount < cautionSummary.totalSeats) {
      cautionBannerBg = 'orange.50';
    }
  }

  const renderUserRow = (user) => (
    <Box key={user.id} bg="white" borderRadius="md">
      <List.Item
        title={`${user.firstName} ${user.lastName}`}
        description={
          user.status === 'COMPANION' ? 'Accompagnateur' : undefined
        }
        titleStyle={{ fontWeight: '600', fontSize: 15 }}
        descriptionStyle={{ fontSize: 12, color: '#888' }}
        left={() => (user.profile?.pictureThumbnail ? (
          <Avatar.Image
            size={48}
            source={{ uri: user.profile.pictureThumbnail }}
            style={{ marginRight: 8 }}
          />
        ) : (
          <Avatar.Text
            size={48}
            label={`${user.firstName[0]}${user.lastName[0]}`}
            style={{ marginRight: 8 }}
          />
        ))}
        right={() => (
          <View style={{ justifyContent: 'center', alignItems: 'center', paddingRight: 8 }}>
            <List.Icon icon="plus-circle" color="#4CAF50" />
          </View>
        )}
        onPress={() => handleAssignSeat(user)}
        style={{ paddingVertical: 8 }}
        disabled={assignLoading}
      />
    </Box>
  );

  return (
    <Portal>
      <Dialog
        visible={displayManageSeatAssignmentModal.display}
        onDismiss={handleClose}
        style={{
          maxHeight: '90%', maxWidth: 700, width: '95%', alignSelf: 'center',
        }}
      >
        <Dialog.Title>
          Attribution des places -
          {trip?.transportation?.name}
        </Dialog.Title>
        <Dialog.ScrollArea style={{ maxHeight: 0.75 * Dimensions.get('window').height }}>
          <ScrollView>
            <VStack space={4} p={4}>
              {/* Caution Summary Banner */}
              {cautionSummary && (
                <Box bg={cautionBannerBg} p={3} borderRadius="md" borderWidth={1} borderColor="gray.200">
                  <HStack justifyContent="space-between" alignItems="center" flexWrap="wrap">
                    <Text style={{ fontWeight: '600', fontSize: 13 }}>
                      {`Cautions : ${cautionSummary.cautionPaidCount}/${cautionSummary.totalSeats} collectées · ${cautionSummary.cautionReturnedCount}/${cautionSummary.totalSeats} restituées`}
                    </Text>
                    <Text style={{ fontSize: 13, color: '#555' }}>
                      {`En cours : ${cautionSummary.totalPending.toFixed(0)}€`}
                    </Text>
                  </HStack>
                </Box>
              )}

              {/* Vehicle Visualization - Hidden during participant selection */}
              {!(selectedSeat && !currentSeatAssignment && !isDriverSeat) && (
                <Box bg="gray.50" p={4} borderRadius="md">
                  <Text style={{ fontWeight: 'bold', marginBottom: 8 }}>
                    Plan du véhicule (
                    {seatAssignments.length}
                    /
                    {trip?.transportation?.seats}
                    {' '}
                    assignées)
                  </Text>
                  {trip?.driver && (
                    <Text style={{ fontSize: 12, color: 'gray', marginBottom: 8 }}>
                      Conducteur:
                      {' '}
                      {trip.driver.firstName}
                      {' '}
                      {trip.driver.lastName}
                      {' '}
                      (Place 1)
                    </Text>
                  )}
                  {trip?.guestDriverName && (
                    <Text style={{ fontSize: 12, color: 'gray', marginBottom: 8 }}>
                      Conducteur:
                      {' '}
                      {trip.guestDriverName}
                      {' '}
                      (Invité, Place 1)
                    </Text>
                  )}
                  <TransportationVisualizerWithAssignments
                    type={trip?.transportation?.type}
                    seats={trip?.transportation?.seats}
                    color={trip?.transportation?.color}
                    seatAssignments={seatAssignments}
                    onSeatPress={handleSeatPress}
                    selectedSeat={selectedSeat}
                    driverSeat={1}
                  />
                </Box>
              )}

              {/* Selected Seat Info */}
              {selectedSeat && (
                <Box bg="blue.50" p={4} borderRadius="md">
                  <HStack justifyContent="space-between" alignItems="center">
                    <Text style={{ fontWeight: 'bold' }}>
                      Place
                      {selectedSeat}
                    </Text>
                    <IconButton
                      icon="close"
                      size={20}
                      onPress={() => {
                        setSelectedSeat(null);
                        setSelectedParticipant(null);
                      }}
                    />
                  </HStack>

                  {isDriverSeat ? (
                    <VStack space={2} mt={2}>
                      <Chip
                        icon="steering"
                        style={{ alignSelf: 'flex-start', backgroundColor: '#2E7D32' }}
                        textStyle={{ color: 'white' }}
                      >
                        Place conducteur
                      </Chip>
                      <Text>
                        {trip?.driver
                          ? `${trip.driver.firstName} ${trip.driver.lastName}`
                          : trip?.guestDriverName || 'Aucun conducteur assigné'}
                      </Text>
                      <Text style={{ fontSize: 12, color: 'gray' }}>
                        La place conducteur est assignée automatiquement et ne peut pas être
                        modifiée ici.
                      </Text>
                    </VStack>
                  ) : currentSeatAssignment ? (
                    <VStack space={2} mt={2}>
                      <Text>
                        {`Assignée à : ${currentSeatAssignment.player.firstName} ${currentSeatAssignment.player.lastName}`}
                        {currentSeatAssignment.isCompanion ? ' (Accompagnateur)' : ''}
                      </Text>
                      <Button
                        mode="outlined"
                        onPress={() => handleUnassignSeat(currentSeatAssignment.id)}
                        loading={unassignLoading}
                        icon="close-circle"
                        textColor="red"
                        style={{ borderColor: 'red' }}
                      >
                        Libérer la place
                      </Button>
                    </VStack>
                  ) : (
                    <Text mt={2}>
                      Sélectionnez un participant ci-dessous pour l'assigner à cette place
                    </Text>
                  )}
                </Box>
              )}

              {/* User Selection — sectioned list */}
              {selectedSeat && !currentSeatAssignment && !isDriverSeat && (
                <Box bg="white" p={4} borderRadius="md" shadow={1}>
                  <Text style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 12 }}>
                    Sélectionner un participant
                  </Text>
                  <Searchbar
                    placeholder="Rechercher..."
                    onChangeText={setSearchQuery}
                    value={searchQuery}
                    style={{ marginBottom: 12 }}
                  />
                  <Box
                    bg="gray.50"
                    borderRadius="md"
                    p={2}
                    style={{ minHeight: 300, maxHeight: 400 }}
                  >
                    <ScrollView>
                      {filteredParticipants.length === 0 && filteredOthers.length === 0 ? (
                        <Box py={8}>
                          <Text style={{ textAlign: 'center', color: 'gray', fontSize: 14 }}>
                            {searchQuery
                              ? 'Aucun utilisateur trouvé'
                              : 'Tous les utilisateurs sont déjà assignés'}
                          </Text>
                        </Box>
                      ) : (
                        <VStack space={1}>
                          {/* Confirmed event participants */}
                          {filteredParticipants.length > 0 && (
                            <>
                              <Text
                                style={{
                                  fontSize: 12,
                                  fontWeight: 'bold',
                                  color: '#555',
                                  paddingHorizontal: 8,
                                  paddingVertical: 4,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                }}
                              >
                                Participants de l'événement
                              </Text>
                              {filteredParticipants.map(renderUserRow)}
                            </>
                          )}

                          {/* Other users — companions, active members not registered */}
                          {filteredOthers.length > 0 && (
                            <>
                              {filteredParticipants.length > 0 && (
                                <Divider style={{ marginVertical: 4 }} />
                              )}
                              <Text
                                style={{
                                  fontSize: 12,
                                  fontWeight: 'bold',
                                  color: '#555',
                                  paddingHorizontal: 8,
                                  paddingVertical: 4,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                }}
                              >
                                Autres utilisateurs
                              </Text>
                              {filteredOthers.map(renderUserRow)}
                            </>
                          )}
                        </VStack>
                      )}
                    </ScrollView>
                  </Box>
                </Box>
              )}

              {/* Currently Assigned Seats Summary - Collapsible */}
              <Box bg="white" borderRadius="md" shadow={1}>
                <List.Accordion
                  title={`Places assignées (${seatAssignments.length})`}
                  titleStyle={{ fontWeight: 'bold', fontSize: 16 }}
                  expanded={isAssignedSeatsExpanded}
                  onPress={() => setIsAssignedSeatsExpanded(!isAssignedSeatsExpanded)}
                  left={(props) => <List.Icon {...props} icon="seat" />}
                >
                  {seatAssignments.length === 0 ? (
                    <Box p={4}>
                      <Text style={{ color: 'gray', textAlign: 'center' }}>
                        Aucune place assignée pour le moment
                      </Text>
                    </Box>
                  ) : (
                    <VStack space={2} p={2}>
                      {seatAssignments.map((assignment) => {
                        const isDriver = assignment.seatNumber === 1;
                        return (
                          <VStack
                            key={assignment.id}
                            bg="gray.50"
                            p={3}
                            borderRadius="md"
                            space={1}
                          >
                            {/* Row 1: seat chip + avatar + name */}
                            <HStack space={3} alignItems="center">
                              <Chip
                                style={{
                                  backgroundColor: isDriver ? '#2E7D32' : '#4CAF50',
                                }}
                                textStyle={{ color: 'white', fontWeight: 'bold' }}
                              >
                                Place
                                {' '}
                                {assignment.seatNumber}
                              </Chip>
                              {assignment.player.profile?.pictureThumbnail ? (
                                <Avatar.Image
                                  size={36}
                                  source={{ uri: assignment.player.profile.pictureThumbnail }}
                                />
                              ) : (
                                <Avatar.Text
                                  size={36}
                                  label={`${assignment.player.firstName[0]}${assignment.player.lastName[0]}`}
                                />
                              )}
                              <Text style={{ fontWeight: '500', flexShrink: 1 }}>
                                {assignment.player.firstName}
                                {' '}
                                {assignment.player.lastName}
                                {isDriver && ' (Conducteur)'}
                              </Text>
                            </HStack>
                            {/* Row 2: companion label + action icons */}
                            {!isDriver && (
                              <HStack justifyContent="space-between" alignItems="center">
                                <View>
                                  {assignment.isCompanion && (
                                    <Chip
                                      style={{ backgroundColor: '#9E9E9E' }}
                                      textStyle={{ color: 'white', fontSize: 10 }}
                                    >
                                      Accompagnateur
                                    </Chip>
                                  )}
                                </View>
                                <HStack alignItems="center">
                                  <CautionIcon
                                    assignment={assignment}
                                    onMarkPaid={() => markCautionPaid({
                                      variables: { seatAssignmentId: assignment.id },
                                    })}
                                    onMarkReturned={() => markCautionReturned({
                                      variables: { seatAssignmentId: assignment.id },
                                    })}
                                    onReset={() => resetCaution({
                                      variables: { seatAssignmentId: assignment.id },
                                    })}
                                  />
                                  <IconButton
                                    icon="close"
                                    size={20}
                                    onPress={() => handleUnassignSeat(assignment.id)}
                                  />
                                </HStack>
                              </HStack>
                            )}
                          </VStack>
                        );
                      })}
                    </VStack>
                  )}
                </List.Accordion>
              </Box>
            </VStack>
          </ScrollView>
        </Dialog.ScrollArea>
        <Dialog.Actions>
          <Button onPress={handleClose}>Fermer</Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );
}

export default ManageSeatAssignmentModal;
